/**
 * "Testar como conversa" (#1012): o mapa aberto no editor, percorrido como uma
 * conversa de WhatsApp. A automação fala, o menu aparece com as opções, e quem
 * testa toca nelas como o cliente. Sem React, sem rede e sem efeito: nada é
 * enviado nem gravado, e não precisa salvar antes.
 *
 * O que depende de dado real (uma regra sobre o evento, uma consulta ao banco
 * do cliente) não é adivinhado: a conversa pergunta a quem testa ("a regra
 * vale?") ou diz que o passo foi pulado.
 */

import type { BlocosDoEditor } from '@cliqcrm/contracts';
import {
  PROXIMO,
  rotuloDaSaidaDoNo,
  saidaDaLigacao,
  saidasDoNo,
  type Configuracao,
} from './edicao';
import { descreverNo, duracao, type NoDoGrafo } from './mapa';
import { SEM_RESPOSTA, ehMenu, prazoSemResposta, temRamos } from './menu';
import { opcoesDoMenu, textosDaLista } from './parametros';

export type QuemFala = 'cliente' | 'automacao' | 'sistema';

export interface Fala {
  readonly quem: QuemFala;
  readonly texto: string;
  /** As opções do menu que a automação mandou. */
  readonly opcoes?: readonly { readonly id: string; readonly rotulo: string }[];
}

/** O que a conversa espera de quem testa para seguir. */
export type Pergunta =
  | { readonly tipo: 'menu'; readonly no: string; readonly semResposta: boolean }
  | {
      readonly tipo: 'escolher';
      readonly no: string;
      readonly texto: string;
      readonly saidas: readonly { saida: string; rotulo: string }[];
    };

export interface EstadoDaConversa {
  readonly falas: readonly Fala[];
  readonly pergunta: Pergunta | null;
  /** Os blocos por onde passou, na ordem. */
  readonly visitados: readonly string[];
}

/** O teto de passos: um mapa com ciclo não trava a tela. */
const MAXIMO_DE_PASSOS = 200;

/** O texto de um parâmetro: o fixo entre aspas, ou um aviso de que é montado na hora. */
export function textoDoParametro(expressao: string | undefined): string {
  const valor = (expressao ?? '').trim();
  const simples = /^'((?:[^'\\]|\\.)*)'$/s.exec(valor);
  if (simples) return simples[1]!.replace(/\\'/g, "'");
  if (valor.startsWith('"')) {
    try {
      const lido: unknown = JSON.parse(valor);
      if (typeof lido === 'string') return lido;
    } catch {
      /* não é um texto fixo */
    }
  }
  return valor ? '(texto montado na hora, com os dados do cliente)' : '';
}

function proximo(c: Configuracao, de: string, saida: string): string | null {
  return c.grafo.ligacoes.find((l) => l.de === de && saidaDaLigacao(l) === saida)?.para ?? null;
}

/** O que uma ação faz, numa frase, quando ela não é uma mensagem ao cliente. */
function fraseDaAcao(
  no: Extract<NoDoGrafo, { tipo: 'acao' }>,
  blocos: BlocosDoEditor | undefined,
): string {
  const acao = blocos?.acoes.find((a) => a.acao === no.acao);
  const titulo = acao?.titulo ?? no.acao;
  const escolhido = (nome: string): string | null => {
    const valor = textoDoParametro(no.parametros[nome]);
    const parametro = acao?.parametros.find((p) => p.nome === nome);
    return parametro?.opcoes?.find((o) => o.valor === valor)?.rotulo ?? (valor || null);
  };
  switch (no.acao) {
    case 'atribuir_conversa':
      return `A conversa passou para ${escolhido('pessoa_id') ?? 'alguém da equipe'}.`;
    case 'encerrar_conversa': {
      const motivo = textoDoParametro(no.parametros['motivo']);
      return `A conversa foi ${escolhido('situacao')?.toLowerCase() ?? 'resolvida'}${motivo ? ` (${motivo})` : ''}.`;
    }
    case 'definir_etiquetas': {
      const etiquetas = textosDaLista(no.parametros['etiquetas'] ?? '') ?? [];
      return etiquetas.length > 0 ? `Etiquetas na conversa: ${etiquetas.join(', ')}.` : titulo;
    }
    default:
      return `${titulo}.`;
  }
}

/**
 * Anda a partir de um bloco até a conversa precisar de quem testa (um menu,
 * uma regra) ou o caminho acabar.
 */
function andar(
  c: Configuracao,
  blocos: BlocosDoEditor | undefined,
  desde: string | null,
  antes: EstadoDaConversa,
): EstadoDaConversa {
  const falas = [...antes.falas];
  const visitados = [...antes.visitados];
  let atual = desde;
  const nesteTrecho = new Set<string>();
  for (let passos = 0; atual !== null && passos < MAXIMO_DE_PASSOS; passos++) {
    const no = c.grafo.nos.find((n) => n.id === atual);
    if (!no) break;
    // O mapa salvo só volta a um bloco pela resposta do cliente: voltar sem ninguém responder é laço.
    if (nesteTrecho.has(no.id)) break;
    nesteTrecho.add(no.id);
    visitados.push(no.id);
    const titulo = descreverNo(no, blocos).titulo;

    if (no.tipo === 'acao') {
      if (no.acao === 'enviar_mensagem') {
        falas.push({
          quem: 'automacao',
          texto: textoDoParametro(no.parametros['texto']) || '(mensagem pronta)',
        });
      } else if (no.acao === 'enviar_menu') {
        const opcoes = (opcoesDoMenu(no.parametros['opcoes'] ?? '') ?? []).map((o) => ({
          id: o.id,
          rotulo: o.rotulo,
        }));
        falas.push({ quem: 'automacao', texto: textoDoParametro(no.parametros['texto']), opcoes });
        if (ehMenu(no) && temRamos(no, c.grafo.ligacoes)) {
          return {
            falas,
            visitados,
            pergunta: {
              tipo: 'menu',
              no: no.id,
              semResposta: ehMenu(no) && prazoSemResposta(no) !== null,
            },
          };
        }
      } else {
        falas.push({ quem: 'sistema', texto: fraseDaAcao(no, blocos) });
      }
      atual = proximo(c, no.id, PROXIMO);
      continue;
    }
    if (no.tipo === 'esperar_tempo') {
      falas.push({
        quem: 'sistema',
        texto: `Espera ${duracao(no.milissegundos)}: no teste, ela passa na hora.`,
      });
      atual = proximo(c, no.id, PROXIMO);
      continue;
    }
    if (no.tipo === 'consulta_sql' || no.tipo === 'para_cada') {
      falas.push({
        quem: 'sistema',
        texto: `${titulo}: lê o banco do cliente, e no teste é pulado.`,
      });
      atual = proximo(c, no.id, PROXIMO);
      continue;
    }
    // Condição, escolha e espera de evento dependem do que acontece de verdade:
    // quem testa decide por onde segue.
    const saidas = saidasDoNo(no, c.grafo.ligacoes, blocos).map((saida) => ({
      saida,
      rotulo: rotuloDaSaidaDoNo(no, saida),
    }));
    const pergunta =
      no.tipo === 'condicao'
        ? `A regra "${descreverNo(no, blocos).resumo}" vale para este cliente?`
        : no.tipo === 'esperar_evento'
          ? `A automação espera: ${descreverNo(no, blocos).resumo}. O que acontece?`
          : `${titulo}: por qual caminho segue?`;
    return { falas, visitados, pergunta: { tipo: 'escolher', no: no.id, texto: pergunta, saidas } };
  }
  if (atual !== null)
    falas.push({
      quem: 'sistema',
      texto: 'O teste parou: o caminho volta a um bloco por onde já passou.',
    });
  falas.push({ quem: 'sistema', texto: 'Fim da automação.' });
  return { falas, visitados, pergunta: null };
}

/** O começo: a primeira mensagem do cliente, quando o gatilho é uma conversa. */
export function comecarConversa(
  c: Configuracao,
  blocos: BlocosDoEditor | undefined,
  primeiraMensagem = 'Oi',
): EstadoDaConversa {
  const g = c.gatilho;
  const doCliente =
    g.tipo === 'evento' &&
    (g.evento === 'conversa.criada' || g.evento === 'conversa.mensagem_recebida');
  const evento =
    g.tipo === 'evento' ? blocos?.eventos.find((e) => e.evento === g.evento)?.titulo : null;
  const inicio: Fala[] = doCliente
    ? [{ quem: 'cliente', texto: primeiraMensagem }]
    : [{ quem: 'sistema', texto: evento ? `Começou: ${evento}.` : 'Começou no horário agendado.' }];
  if (c.condicao?.expressao.trim()) {
    inicio.push({
      quem: 'sistema',
      texto: 'O "Só se" é conferido com dados reais: no teste, ele conta como atendido.',
    });
  }
  return andar(c, blocos, c.grafo.inicio || null, { falas: inicio, pergunta: null, visitados: [] });
}

/** O cliente tocou numa opção do menu (ou escreveu o texto dela). */
export function escolherOpcao(
  c: Configuracao,
  blocos: BlocosDoEditor | undefined,
  estado: EstadoDaConversa,
  opcao: { id: string; rotulo: string },
): EstadoDaConversa {
  if (estado.pergunta?.tipo !== 'menu') return estado;
  const no = estado.pergunta.no;
  const comFala = {
    ...estado,
    falas: [...estado.falas, { quem: 'cliente' as const, texto: opcao.rotulo }],
  };
  // A opção sem ramo próprio segue pelo "Próximo" do menu, como no serviço.
  const para = proximo(c, no, `caso:${opcao.id}`) ?? proximo(c, no, PROXIMO);
  if (para === null) {
    return {
      ...comFala,
      falas: [
        ...comFala.falas,
        {
          quem: 'sistema',
          texto: `A opção "${opcao.rotulo}" não leva a nenhum passo: a automação termina aqui.`,
        },
      ],
      pergunta: null,
    };
  }
  return andar(c, blocos, para, { ...comFala, pergunta: null });
}

/**
 * O cliente escreveu. No WhatsApp, texto digitado nunca é escolha de menu, nem
 * igual ao de uma opção: só o toque na opção vira "O cliente escolheu uma
 * opção do menu". O teste mostra isso, em vez de seguir por um caminho que não
 * aconteceria.
 */
export function escreverComoCliente(
  _c: Configuracao,
  _blocos: BlocosDoEditor | undefined,
  estado: EstadoDaConversa,
  texto: string,
): EstadoDaConversa {
  const limpo = texto.trim();
  if (!limpo) return estado;
  return {
    ...estado,
    falas: [
      ...estado.falas,
      { quem: 'cliente', texto: limpo },
      {
        quem: 'sistema',
        texto:
          estado.pergunta?.tipo === 'menu'
            ? 'Mensagem escrita não escolhe opção: no WhatsApp, a automação continua esperando o cliente tocar numa delas.'
            : 'A automação não está esperando mensagem agora.',
      },
    ],
  };
}

/** Quem testa decidiu por onde segue (regra, escolha, espera) ou que o cliente não respondeu. */
export function seguirPor(
  c: Configuracao,
  blocos: BlocosDoEditor | undefined,
  estado: EstadoDaConversa,
  saida: string,
): EstadoDaConversa {
  const pergunta = estado.pergunta;
  if (!pergunta) return estado;
  const no = c.grafo.nos.find((n) => n.id === pergunta.no);
  const rotulo =
    saida === SEM_RESPOSTA && pergunta.tipo === 'menu'
      ? 'O cliente não respondeu'
      : rotuloDaSaidaDoNo(no, saida);
  const comFala = {
    ...estado,
    pergunta: null,
    falas: [...estado.falas, { quem: 'sistema' as const, texto: `${rotulo}.` }],
  };
  return andar(c, blocos, proximo(c, pergunta.no, saida), comFala);
}
